import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { MODULE_SCHEMAS, MODULE_ORDER } from "../lib/moduleSchemas";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, ArrowRight, Check } from "lucide-react";

export default function Import() {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [module, setModule] = useState("");
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const doPreview = async () => {
    if (!file) { toast.error("Pick a CSV"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const doImport = async () => {
    if (!module) { toast.error("Pick a module"); return; }
    const schema = MODULE_SCHEMAS[module];
    setBusy(true);
    try {
      // Build rows based on mapping
      const rows = (preview.all_rows || []).map((row) => {
        const out = {};
        for (const f of schema.fields) {
          const source = mapping[f.key];
          if (source && row[source] !== undefined && row[source] !== "") {
            out[f.key] = row[source];
          }
        }
        // title mapping
        const titleSrc = mapping.__title;
        out.title = (titleSrc && row[titleSrc]) || (Object.values(out)[0] || "Imported");
        return out;
      });
      const { data } = await api.post("/import/commit", { module, rows });
      setResult(data);
      setStep(3);
      toast.success(`Imported ${data.inserted} rows`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <div className="label-overline">Data Migration</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
          Import From Spreadsheet
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Upload a CSV from your existing Excel organiser. Map columns to fields and commit.
          We'll encrypt sensitive fields automatically.
        </p>
      </div>

      <div className="flex items-center gap-2 text-sm">
        <Step n={1} label="Upload CSV" active={step >= 1} done={step > 1} />
        <ArrowRight className="h-4 w-4 text-slate-400" />
        <Step n={2} label="Map Fields" active={step >= 2} done={step > 2} />
        <ArrowRight className="h-4 w-4 text-slate-400" />
        <Step n={3} label="Done" active={step >= 3} done={step >= 3} />
      </div>

      {step === 1 && (
        <Card className="p-6">
          <Label>CSV or Excel File</Label>
          <Input type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="mt-2"
            data-testid="import-file-input" />
          <div className="text-xs text-slate-500 mt-2">
            Tip: export a sheet from Excel or Google Sheets as .xlsx or .csv
          </div>
          <Button className="mt-6" onClick={doPreview} disabled={!file || busy}
            data-testid="import-preview-button">
            <Upload className="h-4 w-4 mr-2" /> {busy ? "Reading..." : "Preview"}
          </Button>
        </Card>
      )}

      {step === 2 && preview && (
        <>
          <Card className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="font-heading font-semibold">Step 2 · Map fields</div>
                <div className="text-xs text-slate-500 mt-1">
                  {preview.filename} · {preview.row_count} rows · {preview.headers.length} columns
                </div>
              </div>
              <div className="min-w-[240px]">
                <Label>Import into module</Label>
                <Select value={module} onValueChange={setModule}>
                  <SelectTrigger data-testid="import-module-select"><SelectValue placeholder="Choose module" /></SelectTrigger>
                  <SelectContent>
                    {MODULE_ORDER.map((slug) => (
                      <SelectItem key={slug} value={slug}>{MODULE_SCHEMAS[slug].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          {module && (
            <Card className="p-6">
              <div className="font-medium text-sm text-slate-900 mb-3">Field Mapping</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-40 text-xs font-medium text-slate-700">Title (required)</div>
                  <Select value={mapping.__title || ""} onValueChange={(v) => setMapping({ ...mapping, __title: v })}>
                    <SelectTrigger className="flex-1" data-testid="import-map-title"><SelectValue placeholder="Pick column" /></SelectTrigger>
                    <SelectContent>
                      {preview.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {MODULE_SCHEMAS[module].fields.map((f) => (
                  <div key={f.key} className="flex items-center gap-3">
                    <div className="w-40 text-xs text-slate-700 truncate">{f.label}</div>
                    <Select value={mapping[f.key] || ""} onValueChange={(v) => setMapping({ ...mapping, [f.key]: v })}>
                      <SelectTrigger className="flex-1" data-testid={`import-map-${f.key}`}>
                        <SelectValue placeholder="— skip —" />
                      </SelectTrigger>
                      <SelectContent>
                        {preview.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card className="p-0 overflow-hidden">
            <div className="p-4 border-b bg-slate-50 font-medium text-sm">Sample (first 10 rows)</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50/70 border-b">
                    {preview.headers.map((h) => (
                      <th key={h} className="px-3 py-2 text-left font-medium text-slate-700">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.slice(0, 10).map((row, i) => (
                    <tr key={i} className="border-b">
                      {preview.headers.map((h) => (
                        <td key={h} className="px-3 py-2 text-slate-700">{row[h]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="import-back-button">
              Back
            </Button>
            <Button onClick={doImport} disabled={!module || busy}
              data-testid="import-commit-button">
              <Check className="h-4 w-4 mr-2" /> {busy ? "Importing..." : "Commit import"}
            </Button>
          </div>
        </>
      )}

      {step === 3 && result && (
        <Card className="p-10 text-center">
          <div className="h-12 w-12 rounded-full bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center">
            <Check className="h-6 w-6" />
          </div>
          <div className="mt-4 font-heading text-2xl font-semibold">Imported successfully</div>
          <div className="text-sm text-slate-600 mt-1">
            {result.inserted} rows added to {MODULE_SCHEMAS[module].label}.
          </div>
          <Button className="mt-6" onClick={() => { setStep(1); setPreview(null); setResult(null); setFile(null); }}>
            Import another file
          </Button>
        </Card>
      )}
    </div>
  );
}

function Step({ n, label, active, done }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-semibold
        ${done ? "bg-emerald-600 text-white" : active ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-600"}`}>
        {done ? <Check className="h-3 w-3" /> : n}
      </div>
      <div className={`text-xs ${active ? "text-slate-900 font-medium" : "text-slate-500"}`}>{label}</div>
    </div>
  );
}
